import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Scissors, LayoutDashboard, Store, Package, Users, 
  TrendingUp, LogOut, Receipt, ShoppingCart, PanelLeftClose, PanelLeft, ScanBarcode, CalendarDays, UserCheck, Boxes, PackagePlus, Crown, Shield, Gift, Wallet, PieChart, MapPin, ClipboardList, Trophy, BookOpen
} from 'lucide-react';

const allNavItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, roles: ['master_admin', 'admin'] },
  { path: '/admin/appointments', label: 'Citas', icon: CalendarDays, roles: ['master_admin', 'admin'] },
  { path: '/admin/daily-cut', label: 'Corte del Día', icon: Receipt, roles: ['master_admin', 'admin', 'recepcionista'] },
  { path: '/admin/daily-cut-services', label: 'Corte Servicios', icon: Scissors, roles: ['master_admin'] },
  { path: '/admin/daily-cut-products', label: 'Corte Productos (Mes)', icon: Package, roles: ['master_admin'] },
  { path: '/admin/cash-register', label: 'Cortes de Caja', icon: Wallet, roles: ['master_admin', 'admin', 'recepcionista'] },
  { path: '/admin/reports', label: 'Reportes', icon: TrendingUp, roles: ['master_admin', 'admin'] },
  { path: '/admin/reports/balance', label: 'Balance General', icon: BookOpen, roles: ['master_admin', 'admin'] },
  { path: '/admin/partners', label: 'Reparto Utilidades', icon: PieChart, roles: ['master_admin'] },
  { path: '/admin/zones', label: 'Zonas Gerenciales', icon: MapPin, roles: ['master_admin'] },
  { path: '/admin/zones-ranking', label: 'Ranking por Zona', icon: Trophy, roles: ['master_admin'] },
  { path: '/admin/restock-requests', label: 'Solicitudes Inventario', icon: ClipboardList, roles: ['master_admin'] },
  { path: '/admin/loyalty', label: 'Lealtad', icon: Gift, roles: ['master_admin', 'recepcionista'] },
  { path: '/recepcion', label: 'Mi Panel', icon: TrendingUp, roles: ['recepcionista'] },
  { path: '/admin/fixed-expenses', label: 'Gastos Fijos', icon: Receipt, roles: ['master_admin'] }, // Solo Master Admin
  { path: '/admin/branches', label: 'Sucursales', icon: Store, roles: ['master_admin'] }, // Solo Master Admin
  { path: '/admin/services', label: 'Servicios', icon: Scissors, roles: ['master_admin', 'admin'] },
  { path: '/admin/products', label: 'Productos', icon: Package, roles: ['master_admin', 'admin'] },
  { path: '/admin/inventory', label: 'Inv. Productos', icon: ScanBarcode, roles: ['master_admin', 'admin'] },
  { path: '/admin/supplies', label: 'Insumos', icon: Boxes, roles: ['master_admin', 'admin'] },
  { path: '/admin/supplies/inventory', label: 'Inv. Insumos', icon: PackagePlus, roles: ['master_admin', 'admin'] },
  { path: '/admin/barbers', label: 'Barberos y Estilistas', icon: UserCheck, roles: ['master_admin', 'admin'] },
  { path: '/admin/barbers/sales', label: 'Ventas por Barbero', icon: TrendingUp, roles: ['master_admin'] },
  { path: '/admin/users', label: 'Encargados', icon: Users, roles: ['master_admin', 'admin'] },
];

const ROLE_LABELS = {
  master_admin: { label: 'Master Admin', color: 'text-gold', bgColor: 'bg-gold/20', icon: Crown },
  admin: { label: 'Admin Sucursal', color: 'text-blue-400', bgColor: 'bg-blue-400/20', icon: Shield },
};

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => item.roles.includes(user?.role));
  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.admin;
  const RoleIcon = roleInfo.icon;

  return (
    <div className="min-h-screen flex bg-[#050505]">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} border-r border-zinc-800 flex flex-col transition-all duration-300`}>
        {/* Logo */}
        <div className="h-16 border-b border-zinc-800 flex items-center px-3 gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_8f620d75-9fbb-40ab-bbdd-47d8eeebdc0d/artifacts/z1jrqp78_IMG_0475.jpeg" 
            alt="The Holland"
            className="w-10 h-10 object-contain flex-shrink-0"
          />
          {sidebarOpen && (
            <div className="overflow-hidden">
              <p className="font-heading text-sm text-white whitespace-nowrap">The Holland</p>
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest">Barbershop</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                    isActive
                      ? 'bg-gold/10 text-gold'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                  } ${!sidebarOpen ? 'justify-center' : ''}`}
                  data-testid={`nav-${item.path.replace('/admin/', '') || 'dashboard'}`}
                  title={!sidebarOpen ? item.label : ''}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Info & Actions */}
        <div className="p-3 border-t border-zinc-800 space-y-3">
          <Link
            to="/pos"
            className={`flex items-center justify-center gap-2 w-full h-10 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-sm transition-colors`}
            data-testid="admin-goto-pos"
            title={!sidebarOpen ? 'Ir al POS' : ''}
          >
            <ShoppingCart className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm">Ir al POS</span>}
          </Link>
          
          {sidebarOpen && (
            <div className="space-y-2">
              {/* Role Badge */}
              <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm ${roleInfo.bgColor}`}>
                <RoleIcon className={`w-4 h-4 ${roleInfo.color}`} />
                <span className={`text-xs font-medium ${roleInfo.color}`}>{roleInfo.label}</span>
              </div>
              
              {/* Branch Info (for admin only) */}
              {user?.role === 'admin' && user?.branch_name && (
                <div className="px-2 py-1 bg-zinc-800/50 rounded-sm">
                  <p className="text-xs text-zinc-500">Sucursal</p>
                  <p className="text-sm text-white truncate">{user.branch_name}</p>
                </div>
              )}
              
              {/* User Info */}
              <div className="flex items-center gap-3 px-2">
                <div className={`w-8 h-8 rounded-full ${roleInfo.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <span className={`${roleInfo.color} text-sm font-semibold`}>
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-500 truncate">@{user?.username}</p>
                </div>
              </div>
            </div>
          )}
          
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full text-zinc-400 hover:text-red-500 hover:bg-red-500/10 ${sidebarOpen ? 'justify-start' : 'justify-center px-0'}`}
            data-testid="admin-logout-btn"
            title={!sidebarOpen ? 'Cerrar Sesión' : ''}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span className="ml-2">Cerrar Sesión</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col">
        {/* Toggle Button Bar */}
        <div className="h-12 border-b border-zinc-800 flex items-center justify-between px-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            data-testid="toggle-sidebar-btn"
            title={sidebarOpen ? 'Ocultar menú' : 'Mostrar menú'}
          >
            {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
          </button>
          
          {/* Branch indicator for non-master admins */}
          {user?.role === 'admin' && user?.branch_name && (
            <div className="flex items-center gap-2 text-sm">
              <Store className="w-4 h-4 text-zinc-500" />
              <span className="text-zinc-400">Vista: <span className="text-white">{user.branch_name}</span></span>
            </div>
          )}
          {user?.role === 'master_admin' && (
            <div className="flex items-center gap-2 text-sm">
              <Crown className="w-4 h-4 text-gold" />
              <span className="text-zinc-400">Vista: <span className="text-gold">Todas las Sucursales</span></span>
            </div>
          )}
        </div>
        <div className="flex-1 p-8 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
